/**
 * Zoom level utilities for consistent rounding and calculations
 * Provides centralized logic to prevent code duplication across components
 */
import { PREVIEW_PANEL_HEIGHT } from '@/constants/ui';

/**
 * Bottom padding (px) to pass to `zoomTo` so a freshly-opened detail panel does
 * not cover the zoomed-to geometry. On mobile the detail panel slides up to
 * `PREVIEW_PANEL_HEIGHT` from the bottom; on desktop the side panel does not
 * obstruct the map, so no padding is reserved.
 *
 * Single source of truth for the `isMobile ? PREVIEW_PANEL_HEIGHT : 0` rule
 * shared by list navigation, map-marker clicks, and the public share map.
 */
export function previewZoomBottomPadding(isMobile: boolean): number {
  return isMobile ? PREVIEW_PANEL_HEIGHT : 0;
}

/**
 * Round zoom level to specified precision to reduce re-renders
 * Default precision of 0.5 provides optimal balance between smoothness and performance
 *
 * @param zoom - Raw zoom level from map
 * @param precision - Rounding precision (default: 0.5)
 * @returns Rounded zoom level
 *
 * @example
 * roundZoom(12.34) // 12.5
 * roundZoom(12.34, 1) // 12
 * roundZoom(12.34, 0.25) // 12.25
 */
export function roundZoom(zoom: number, precision = 0.5): number {
  const factor = 1 / precision;
  return Math.round(zoom * factor) / factor;
}

/**
 * Clamp a zoom level into an inclusive [min, max] range.
 *
 * @example
 * clampZoom(3, 4, 18) // 4
 * clampZoom(20, 4, 18) // 18
 */
export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(Math.max(zoom, min), max);
}

/**
 * Check if zoom level has changed significantly enough to warrant re-render
 *
 * @param prevZoom - Previous zoom level
 * @param newZoom - New zoom level
 * @param threshold - Minimum difference to consider as change (default: 0.5)
 * @returns true if zoom changed significantly
 *
 * @example
 * hasZoomChanged(12.0, 12.2, 0.5) // false - change too small
 * hasZoomChanged(12.0, 12.6, 0.5) // true - significant change
 */
export function hasZoomChanged(
  prevZoom: number,
  newZoom: number,
  threshold = 0.5
): boolean {
  return Math.abs(prevZoom - newZoom) >= threshold;
}

/**
 * Get zoom level category for conditional rendering logic
 *
 * @param zoom - Current zoom level
 * @returns Zoom category: 'far' | 'medium' | 'close' | 'very-close'
 *
 * @example
 * getZoomCategory(5) // 'far'
 * getZoomCategory(10) // 'medium'
 * getZoomCategory(14) // 'close'
 * getZoomCategory(18) // 'very-close'
 */
export function getZoomCategory(
  zoom: number
): 'far' | 'medium' | 'close' | 'very-close' {
  if (zoom < 8) return 'far';
  if (zoom < 12) return 'medium';
  if (zoom < 16) return 'close';
  return 'very-close';
}
